import { useMemo } from 'react';
import { useGetRoomsQuery, useGetBookingsQuery } from '../api';
import { Room, RoomKind } from '../api/rooms/types';
import { useTranslation } from './useTranslation';

const roomKind = (room: Room): RoomKind => (room.type === 'coworking' ? 'coworking' : 'conference');

interface RoomTranslations {
    [roomId: number]: {
        name: string;
        description: string;
    };
}

/**
 * Получение переводов для переговорных
 */
const getRoomTranslations = (t: ReturnType<typeof useTranslation>['t']): RoomTranslations => ({
    2: {
        name: t.pages.office.rooms.charlemagne.name,
        description: t.pages.office.rooms.charlemagne.description,
    },
    3: {
        name: t.pages.office.rooms.ambiorix.name,
        description: t.pages.office.rooms.ambiorix.description,
    },
});

/**
 * Хук для получения комнат с переводами.
 * Без kind возвращает все комнаты. conference — переговорные, coworking — коворкинг.
 */
export const useRooms = (kind?: RoomKind) => {
    const { t } = useTranslation();
    const { data: rooms, isLoading, error } = useGetRoomsQuery();

    // Переводы для переговорных по ID
    const roomTranslations: RoomTranslations = useMemo(() => getRoomTranslations(t), [t]);

    // Обогащаем данные переводами
    const roomsWithTranslations = useMemo(() => {
        if (!rooms) return undefined;

        return rooms.map((room) => {
            const translation = roomTranslations[room.id];
            if (translation) {
                return {
                    ...room,
                    name: translation.name,
                    description: translation.description,
                };
            }
            return room;
        });
    }, [rooms, roomTranslations]);

    const visibleRooms = useMemo(() => {
        if (!roomsWithTranslations || !kind) return roomsWithTranslations;
        return roomsWithTranslations.filter((room) => roomKind(room) === kind);
    }, [roomsWithTranslations, kind]);

    return {
        rooms: visibleRooms,
        isLoading,
        error,
    };
};

export const useRoomById = (roomId: number) => {
    const { rooms, isLoading, error } = useRooms();

    const room = useMemo(() => {
        if (!rooms) return null;
        return rooms.find((r) => r.id === roomId);
    }, [rooms, roomId]);

    return {
        room,
        isLoading,
        error,
    };
};

/**
 * Хук для получения бронирований с переводами
 */
export const useBookings = () => {
    const { t } = useTranslation();
    const { data: bookings, isLoading, error } = useGetBookingsQuery();

    // Переводы для переговорных по ID
    const roomTranslations: RoomTranslations = useMemo(() => getRoomTranslations(t), [t]);

    // Обогащаем данные переводами
    const bookingsWithTranslations = useMemo(() => {
        if (!bookings) return undefined;

        return bookings.map((booking) => {
            const translation = roomTranslations[booking.room_id];
            if (translation) {
                return {
                    ...booking,
                    room_name: translation.name,
                    room_description: translation.description,
                };
            }
            return booking;
        });
    }, [bookings, roomTranslations]);

    return {
        bookings: bookingsWithTranslations,
        isLoading,
        error,
    };
};
